import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import styled from 'styled-components';
import { useAuth } from '../../context/AuthContext';

// Opplane logo
import opplaneLogo from '../../assets/opplane-logo.png';

const Register = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  
  const { requestRegistration } = useAuth();
  const navigate = useNavigate();
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email || !password) {
      setErrorMessage('Please enter both email and password');
      return;
    }
    
    // Validate email domain
    const domain = email.split('@')[1];
    if (!domain) {
      setErrorMessage('Invalid email format');
      return;
    }
    
    // Check if domain is allowed
    const isAdmin = domain === 'opplane.com';
    const isClient = domain === 'verizon.com' || domain === 'verizonwireless.com';
    
    if (!isAdmin && !isClient) {
      setErrorMessage('Email domain not authorized. Only @opplane.com, @verizon.com, and @verizonwireless.com domains are allowed.');
      return;
    }
    
    // Password validation
    if (password.length < 8) {
      setErrorMessage('Password must be at least 8 characters long');
      return;
    }
    
    // Check for uppercase, lowercase, number, and special character
    const hasUppercase = /[A-Z]/.test(password);
    const hasLowercase = /[a-z]/.test(password);
    const hasNumber = /[0-9]/.test(password);
    const hasSpecial = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password);
    
    if (!hasUppercase || !hasLowercase || !hasNumber || !hasSpecial) {
      setErrorMessage('Password must contain at least one uppercase letter, one lowercase letter, one number, and one special character');
      return;
    }
    
    try {
      setIsSubmitting(true);
      setErrorMessage('');
      
      const result = await requestRegistration(email, password);
      
      if (result) {
        setSuccessMessage('Registration request submitted successfully. An administrator will review your request.');
        setEmail('');
        setPassword('');
      }
    } catch (err) {
      console.error('Registration error:', err);
      setErrorMessage(err.response?.data?.message || 'Registration failed. Please try again later.');
    } finally {
      setIsSubmitting(false);
    }
  };
  
  return (
    <RegisterContainer>
      <RegisterCard>
        <LogoContainer>
          <OpplaneLogo src={opplaneLogo} alt="Opplane Logo" />
        </LogoContainer>
        
        {successMessage ? (
          <SuccessContainer>
            <SuccessMessage>{successMessage}</SuccessMessage>
            <LoginLink to="/login">Return to Login</LoginLink>
          </SuccessContainer>
        ) : (
          <RegisterForm onSubmit={handleSubmit}>
            <FormTitle>Request Registration</FormTitle>
            
            <FormGroup>
              <Label htmlFor="email">Email</Label>
              <Input
                type="email"
                id="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Enter your email"
                disabled={isSubmitting}
                required
              />
            </FormGroup>
            
            <FormGroup>
              <Label htmlFor="password">Password</Label>
              <Input
                type="password"
                id="password"
                value={password}
                onChange={(e) => {
                  setPassword(e.target.value);
                  if (errorMessage) setErrorMessage('');
                }}
                placeholder="Enter your password"
                disabled={isSubmitting}
                required
              />
            </FormGroup>
            
            <PasswordRequirements>
              <RequirementTitle>Password Requirements:</RequirementTitle>
              <RequirementList>
                <Requirement>At least 8 characters long</Requirement>
                <Requirement>At least one uppercase letter</Requirement>
                <Requirement>At least one lowercase letter</Requirement>
                <Requirement>At least one number</Requirement>
                <Requirement>At least one special character</Requirement>
              </RequirementList>
            </PasswordRequirements>
            
            {errorMessage && <ErrorMessage>{errorMessage}</ErrorMessage>}

            <RegisterButton type="submit" disabled={isSubmitting}>
              {isSubmitting ? 'Submitting...' : 'Request Registration'}
            </RegisterButton>
            
            <LoginLink to="/login">Already have an account? Log in</LoginLink>
          </RegisterForm>
        )}
      </RegisterCard>
    </RegisterContainer>
  );
};

// Styled components
const RegisterContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 80vh;
  padding: ${props => props.theme.spacing.lg};
`;

const RegisterCard = styled.div`
  background-color: ${props => props.theme.colors.verizonWhite};
  border-radius: ${props => props.theme.borderRadius.medium};
  box-shadow: ${props => props.theme.shadows.large};
  width: 100%;
  max-width: 450px;
  padding: ${props => props.theme.spacing.xl};
`;

const LogoContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const RegisterForm = styled.form`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const FormTitle = styled.h2`
  font-size: ${props => props.theme.fontSizes.large};
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.md};
  text-align: center;
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: ${props => props.theme.spacing.md};
`;

const Label = styled.label`
  font-size: ${props => props.theme.fontSizes.medium};
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
`;

const Input = styled.input`
  padding: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.small};
  font-size: ${props => props.theme.fontSizes.medium};
  transition: ${props => props.theme.transitions.short};
  
  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.opplaneBlue};
    box-shadow: 0 0 0 2px rgba(0, 102, 204, 0.2);
  }
  
  &:disabled {
    background-color: ${props => props.theme.colors.background};
    cursor: not-allowed;
  }
`;

const RegisterButton = styled.button`
  background-color: ${props => props.theme.colors.opplaneBlue};
  color: ${props => props.theme.colors.verizonWhite};
  border: none;
  border-radius: ${props => props.theme.borderRadius.small};
  padding: ${props => props.theme.spacing.md};
  font-size: ${props => props.theme.fontSizes.medium};
  font-weight: bold;
  cursor: pointer;
  transition: ${props => props.theme.transitions.short};
  margin-top: ${props => props.theme.spacing.md};
  
  &:hover:not(:disabled) {
    background-color: ${props => props.theme.colors.opplaneDarkBlue};
  }
  
  &:disabled {
    background-color: rgba(0, 102, 204, 0.5);
    cursor: not-allowed;
  }
`;

const PasswordRequirements = styled.div`
  background-color: rgba(0, 102, 204, 0.05);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.md};
`;

const RequirementTitle = styled.p`
  font-weight: bold;
  margin-bottom: ${props => props.theme.spacing.xs};
  color: ${props => props.theme.colors.text};
`;

const RequirementList = styled.ul`
  margin: 0;
  padding-left: ${props => props.theme.spacing.lg};
`;

const Requirement = styled.li`
  font-size: ${props => props.theme.fontSizes.small};
  color: ${props => props.theme.colors.textSecondary};
`;

const ErrorMessage = styled.div`
  color: ${props => props.theme.colors.error};
  background-color: rgba(0, 102, 204, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const SuccessContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const SuccessMessage = styled.div`
  color: ${props => props.theme.colors.success};
  background-color: rgba(0, 153, 51, 0.1);
  padding: ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.small};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const LoginLink = styled(Link)`
  color: ${props => props.theme.colors.opplaneBlue};
  text-align: center;
  margin-top: ${props => props.theme.spacing.md};
  text-decoration: none;
  
  &:hover {
    text-decoration: underline;
  }
`;

const OpplaneLogo = styled.img`
  height: 20px;
`;

export default Register;
